import * as api from '@api';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@/hooks/useApi';
import useToast from '@/hooks/useToast';
import useNetworkCompaniesList from './useNetworkCompaniesList';
import useTranslator from './useTranslator';

export default function useComment({
  questionnaireFormQuestionId,
  requestId,
  onCommentAdded,
}) {
  const { toast } = useToast();
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [sortByNewest, setSortByNewest] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const commentsBoxRef = useRef(null);
  const [emptyCommentError, setEmptyCommentError] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentToPerformAction, setCommentToPerformAction] = useState(null);
  const networkCompaniesList = useNetworkCompaniesList();
  const { getTranslation } = useTranslator({ page: 'comment-section' });

  const { data } = useQuery(
    ['getComments', questionnaireFormQuestionId],
    () =>
      api.backend.questionCommentsList(questionnaireFormQuestionId, requestId),
    {
      gcTime: 20,
      enabled: isExpanded,
    },
  );

  // The error toast used to live in the query's `onSuccess` — React Query v5
  // dropped that option, so it moves into the effect that already reads `data`.
  // The early return is new: the fetch wrapper resolves rather than throws on a
  // 4xx, so the mapping below used to run against an error body and blow up on
  // `data.data.data`.
  useEffect(() => {
    if (!data) return;

    if (data.status >= 400) {
      toast.error(data.error.message);
      return;
    }

    setComments(
      data.data.data.map(d => ({
        ...d,
        content: d.questionComment,
        commentedAt: d.createdAt,
        commentedByUserId: d?.updatedByUserId,
        commentedByCompanyId: d?.updatedByCompanyId,
        commentedByUserName: d?.updatedByUserName,
        commentedByCompanyName: d?.updatedByCompanyName,
      })),
    );
    onCommentAdded(data.data.data.length);
  }, [data]);

  const getCommentImageUrl = commentedByCompanyId => {
    const company = networkCompaniesList.find(
      company => company.id === commentedByCompanyId,
    );
    return company?.featuredImageFileUrl;
  };
  const handleSubmitComment = () => {
    if (commentText.trim().length < 1) setEmptyCommentError(true);
    setSubmittingComment(true);
    return api.backend
      .questionCommentCreate(questionnaireFormQuestionId, requestId, {
        questionComment: commentText,
      })
      .then(addCommentResponse => {
        if (addCommentResponse.status === 201) {
          // The migrated route answers `{ metadata, data }`, so the created
          // comment is one level deeper than the legacy bare body was.
          const created = addCommentResponse.data?.data;
          setComments([
            ...comments,
            {
              ...created,
              content: created?.questionComment,
              commentedAt: created?.createdAt,
              commentedByUserId: created?.updatedByUserId,
              commentedByCompanyId: created?.updatedByCompanyId,
              commentedByUserName: created?.updatedByUserName,
              commentedByCompanyName: created?.updatedByCompanyName,
            },
          ]);
          setCommentText('');
          onCommentAdded([...comments, created].length);
          toast.success(getTranslation('comment.action.respond.success'), {
            position: 'bottom-center',
          });
        } else {
          toast.error(addCommentResponse.error, {
            position: 'bottom-center',
          });
        }
      })
      .catch(error => {
        toast.error(error, {
          position: 'bottom-center',
        });
      })
      .finally(() => {
        setSubmittingComment(false);
      });
  };

  const handleTextareaKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmitComment();
    }
  };

  const handleEditComment = () => {
    const commentFound = commentToPerformAction;
    delete commentFound.editAction;
    const foundCommentIndex = comments.findIndex(
      comment => comment.id === commentFound.id,
    );
    if (foundCommentIndex !== -1) {
      const allComments = [...comments];
      allComments[foundCommentIndex] = { ...commentFound };
      return api.backend
        .questionCommentUpdate(
          questionnaireFormQuestionId,
          requestId,
          comments[foundCommentIndex].id,
          { questionComment: commentFound.content },
        )
        .then(response => {
          // The migrated route answers 200; the legacy one hard-coded 201 on a
          // PATCH.
          if (response.status === 200) {
            setComments([...allComments]);
            setCommentToPerformAction(null);
          } else {
            toast.error(response.error, {
              position: 'bottom-center',
            });
          }
        })
        .catch(error => {
          toast.error(error, {
            position: 'bottom-center',
          });
        })
        .finally(() => {
          setSubmittingComment(false);
        });
    }
  };

  const handleDeleteComment = () => {
    const commentFound = commentToPerformAction;
    const foundCommentIndex = comments.findIndex(
      comment => comment.id === commentFound.id,
    );
    if (foundCommentIndex !== -1) {
      return api.backend
        .questionCommentDelete(
          questionnaireFormQuestionId,
          requestId,
          commentFound.id,
        )
        .then(deleteCommentResponse => {
          if (deleteCommentResponse.status === 204) {
            comments.splice(foundCommentIndex, 1);
            setComments(comments);
            setCommentToPerformAction(null);
            toast.success(getTranslation('comment.action.respond.deleted'), {
              position: 'bottom-center',
            });
          } else {
            toast.error(deleteCommentResponse.error, {
              position: 'bottom-center',
            });
          }
        })
        .catch(error => {
          toast.error(error, {
            position: 'bottom-center',
          });
        })
        .finally(() => {
          setSubmittingComment(false);
        });
    }
  };

  return {
    commentText,
    comments,
    sortByNewest,
    setCommentText,
    setComments,
    commentsBoxRef,
    setSortByNewest,
    isExpanded,
    setIsExpanded,
    handleSubmitComment,
    emptyCommentError,
    setEmptyCommentError,
    submittingComment,
    setSubmittingComment,
    handleTextareaKeyDown,
    getCommentImageUrl,
    commentToPerformAction,
    setCommentToPerformAction,
    handleEditComment,
    handleDeleteComment,
  };
}
